import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { requestLoginOTP, verifyLoginOTP, saveSession } from "../service/auth";
import "../styles/Auth.css";

const BRAND_ICON = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();

  /* ── State ── */
  const [step, setStep] = useState(1);           // 1 = email, 2 = OTP
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs = useRef([]);
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  /* ── Cooldown timer ── */
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  /* ── Step 1: send OTP ── */
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) { setError("Please enter a valid email."); return; }
    setLoading(true);
    try {
      await requestLoginOTP(email);
      setStep(2);
      setResendCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.message || "Failed to send OTP. Check your email.");
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP input handling ── */
  const handleOtpChange = (idx, val) => {
    const c = val.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[idx] = c;
    setOtp(next);
    if (c && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text.length) {
      const next = text.split("").concat(["", "", "", "", "", ""]).slice(0, 6);
      setOtp(next);
      otpRefs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  /* ── Step 2: verify OTP ── */
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter the full 6-digit OTP."); return; }
    setLoading(true);
    try {
      const user = await verifyLoginOTP(email, code);
      saveSession(user);
      navigate("/contracts");
    } catch (err) {
      setError(err.message || "Invalid OTP. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await requestLoginOTP(email);
      setResendCooldown(60);
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {error && (
          <div className="auth-error" style={{ marginBottom: 16 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {/* ── Step 1: Email ── */}
        {step === 1 && (
          <form className="auth-form" onSubmit={handleSendOTP}>
            <div className="auth-form__group">
              <label className="auth-form__label">Email Address</label>
              <input
                ref={emailRef}
                className="auth-form__input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? <><div className="auth-spinner" /> Sending OTP…</> : "Send OTP →"}
            </button>

            <div className="auth-divider">or</div>

            <p className="auth-hint">
              Don't have an account?{" "}
              <Link to="/register">Create one</Link>
            </p>
          </form>
        )}

        {/* ── Step 2: OTP ── */}
        {step === 2 && (
          <form className="auth-form" onSubmit={handleVerifyOTP}>
            <p className="auth-hint" style={{ marginBottom: 4 }}>
              OTP sent to
            </p>
            <div className="auth-email-chip">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {email}
            </div>

            <div className="auth-otp-row" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  className={`auth-otp-input ${digit ? "filled" : ""}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? <><div className="auth-spinner" /> Verifying…</> : "Verify & Sign In"}
            </button>

            <p className="auth-hint">
              Didn't receive it?{" "}
              <button type="button" onClick={handleResend} disabled={resendCooldown > 0 || loading}>
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
              </button>
            </p>

            <p className="auth-hint">
              <button type="button" onClick={() => { setStep(1); setOtp(["","","","","",""]); setError(""); }}>
                ← Change email
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
